import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Save, Eye, AlertCircle, Plus, Trash2, Upload, X} from 'lucide-react'

interface PageSEO {
  _id?: string
  url: string
  language: "zh" | "en"
  headContent: string
  pageName: string
  ogImage?: string
  status: "active" | "draft"
  updatedAt?: Date
  updatedBy?: string
}

const PageSEOManager: React.FC = () => {
  const [pages, setPages] = useState<PageSEO[]>([])
  const [selectedPage, setSelectedPage] = useState<string>("")
  const [headContent, setHeadContent] = useState<string>("")
  const [pageName, setPageName] = useState<string>("")
  const [url, setUrl] = useState<string>("")
  const [language, setLanguage] = useState<"zh" | "en">("zh")
  const [ogImage, setOgImage] = useState<string>("")
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // 載入所有頁面
  useEffect(() => {
    console.log("🚀 PageSEOManager mounted")
    console.log("🔍 lumi object:", lumi)
    console.log("🔍 lumi.entities:", lumi.entities)
    console.log("🔍 lumi.entities.page_seo:", lumi.entities.page_seo)
    loadPages()
  }, [])

  const loadPages = async () => {
    console.log("📥 開始載入頁面...")
    setIsLoading(true)
    try {
      const result = await lumi.entities.page_seo.list({
        sort: { url: 1 }
      })
      console.log("✅ 載入成功，頁面數量:", result.list.length)
      console.log("📄 頁面資料:", result.list)
      setPages(result.list)
    } catch (error) {
      console.error("❌ 載入失敗，詳細錯誤:", error)
      console.error("❌ 錯誤類型:", typeof error)
      console.error("❌ 錯誤訊息:", error instanceof Error ? error.message : String(error))
      // 如果是空資料表，不顯示錯誤
      if (error instanceof Error && error.message.includes("not found")) {
        console.log("ℹ️ 資料表為空，這是正常的")
        setPages([])
      } else {
        alert("❌ 載入失敗：" + (error instanceof Error ? error.message : "未知錯誤"))
      }
    } finally {
      console.log("🏁 載入完成，isLoading = false")
      setIsLoading(false)
    }
  }

  // 選擇頁面時載入內容
  const handlePageSelect = (pageId: string) => {
    console.log('📄 選擇頁面:', pageId)
    setSelectedPage(pageId)
    
    if (pageId === "new") {
      // 新增頁面
      console.log('➕ 新增頁面模式')
      setHeadContent("")
      setPageName("")
      setUrl("")
      setLanguage("zh")
      setOgImage("")
      console.log('✅ 表單已清空')
    } else {
      const page = pages.find(p => p._id === pageId)
      console.log('📋 找到頁面資料:', page)
      
      if (page) {
        setHeadContent(page.headContent)
        setPageName(page.pageName)
        setUrl(page.url)
        setLanguage(page.language)
        const imageUrl = page.ogImage || ""
        setOgImage(imageUrl)
        
        console.log('✅ 表單已載入:')
        console.log('  - pageName:', page.pageName)
        console.log('  - url:', page.url)
        console.log('  - language:', page.language)
        console.log('  - ogImage:', imageUrl)
        console.log('  - headContent 長度:', page.headContent.length)
      }
    }
  }

  // 上傳圖片
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    console.log('📤 開始上傳圖片流程...')
    console.log('📄 選擇的檔案:', file)
    
    if (!file) {
      console.log('❌ 沒有選擇檔案')
      return
    }

    console.log('📊 檔案資訊:', {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeInMB: (file.size / 1024 / 1024).toFixed(2) + 'MB'
    })

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      console.log('❌ 檔案類型錯誤:', file.type)
      alert('❌ 請選擇圖片檔案')
      return
    }

    // 檢查檔案大小（限制 5MB）
    if (file.size > 5 * 1024 * 1024) {
      console.log('❌ 檔案太大:', file.size)
      alert('❌ 圖片大小不能超過 5MB')
      return
    }

    console.log('✅ 檔案驗證通過，開始上傳...')
    setIsUploading(true)
    
    try {
      console.log('🚀 調用 lumi.tools.file.upload...')
      const result = await lumi.tools.file.upload([file])
      
      console.log('📦 上傳完成，完整回應:', result)
      console.log('🔗 result[0]:', result[0])
      console.log('📊 result 陣列長度:', result?.length)
      
      if (result && result.length > 0 && result[0].fileUrl) {
        const uploadedUrl = result[0].fileUrl
        console.log('✅ 圖片上傳成功!')
        console.log('🔗 圖片 URL:', uploadedUrl)
        
        setOgImage(uploadedUrl)
        console.log('✅ State 已更新, ogImage =', uploadedUrl)
        
        // 延遲一點再檢查 state
        setTimeout(() => {
          console.log('🔍 延遲檢查 - 當前 ogImage state:', ogImage)
        }, 100)
        
        alert('✅ 圖片上傳成功！\n\nURL: ' + uploadedUrl.substring(0, 50) + '...')
      } else if (result && result.length > 0 && result[0].uploadError) {
        console.error('❌ 上傳失敗:', result[0].uploadError)
        alert('❌ 上傳失敗：' + result[0].uploadError)
      } else {
        console.error('❌ 上傳結果異常：沒有 fileUrl')
        console.error('完整結果:', JSON.stringify(result, null, 2))
        alert('❌ 上傳失敗：沒有收到圖片 URL')
      }
    } catch (error) {
      console.error('❌ 圖片上傳失敗，錯誤詳情:')
      console.error('錯誤類型:', typeof error)
      console.error('錯誤物件:', error)
      console.error('錯誤訊息:', error instanceof Error ? error.message : String(error))
      console.error('錯誤堆疊:', error instanceof Error ? error.stack : '無')
      alert('❌ 圖片上傳失敗，請稍後再試\n\n錯誤: ' + (error instanceof Error ? error.message : '未知錯誤'))
    } finally {
      setIsUploading(false)
      console.log('🏁 上傳流程結束')
    }
  }

  // 刪除圖片
  const handleImageRemove = () => {
    console.log('🗑️ 刪除圖片，當前 URL:', ogImage)
    setOgImage('')
    console.log('✅ 圖片已清除')
    alert('✅ 圖片已移除')
  }

  // 儲存
  const handleSave = async () => {
    if (!headContent.trim()) {
      alert("❌ 請輸入 <head> 內容")
      return
    }

    if (!pageName.trim()) {
      alert("❌ 請輸入頁面名稱")
      return
    }

    if (!url.trim()) {
      alert("❌ 請輸入頁面路徑")
      return
    }

    setIsSaving(true)
    try {
      if (selectedPage && selectedPage !== "new") {
        // 更新
        await lumi.entities.page_seo.update(selectedPage, {
          headContent,
          pageName,
          url,
          language,
          ogImage: ogImage || undefined,
          updatedAt: new Date(),
          updatedBy: lumi.auth.user?.userId || "system"
        })
        alert("✅ 更新成功！")
      } else {
        // 新增
        await lumi.entities.page_seo.create({
          url,
          language,
          headContent,
          pageName,
          ogImage: ogImage || undefined,
          status: "active",
          updatedAt: new Date(),
          updatedBy: lumi.auth.user?.userId || "system"
        })
        alert("✅ 新增成功！")
        setSelectedPage("") // 清空選擇
      }
      
      await loadPages() // 重新載入列表
    } catch (error) {
      console.error("儲存失敗:", error)
      alert("❌ 儲存失敗，請稍後再試")
    } finally {
      setIsSaving(false)
    }
  }

  // 刪除
  const handleDelete = async () => {
    if (!selectedPage || selectedPage === "new") return

    if (!confirm("確定要刪除此頁面的 SEO 設定嗎？")) return

    try {
      await lumi.entities.page_seo.delete(selectedPage)
      alert("✅ 刪除成功！")
      setSelectedPage("")
      await loadPages()
    } catch (error) {
      console.error("刪除失敗:", error)
      alert("❌ 刪除失敗，請稍後再試")
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 bg-black min-h-screen flex items-center justify-center">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* 頁面標題 */}
      <div className="mb-8">
        <h2 className="text-3xl font-light tracking-wider text-gray-100 mb-2">
          分頁 SEO 管理
        </h2>
        <p className="text-gray-400">直接貼上完整的 meta 和 schema 內容</p>
      </div>

      {/* 頁面選擇 */}
      <div className="mb-6">
        <label className="block text-sm text-gray-400 mb-2">選擇頁面</label>
        <select
          value={selectedPage}
          onChange={(e) => handlePageSelect(e.target.value)}
          className="w-full bg-gray-950 border border-gray-800 text-gray-100 px-4 py-3 focus:border-gray-600 focus:outline-none"
        >
          <option value="">-- 選擇頁面 --</option>
          <option value="new">➕ 新增頁面</option>
          {pages.map(page => (
            <option key={page._id} value={page._id}>
              {page.pageName} ({page.url}) [{page.language.toUpperCase()}]
            </option>
          ))}
        </select>
      </div>

      {/* 編輯區域 */}
      {selectedPage && (
        <>
          {/* 頁面基本資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">頁面名稱 *</label>
              <input
                type="text"
                value={pageName}
                onChange={(e) => setPageName(e.target.value)}
                placeholder="例如：信義店、首頁"
                className="w-full bg-gray-950 border border-gray-800 text-gray-100 px-4 py-3 focus:border-gray-600 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">頁面路徑 *</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/zh/store/xinyi"
                className="w-full bg-gray-950 border border-gray-800 text-gray-100 px-4 py-3 focus:border-gray-600 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">語言 *</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as "zh" | "en")}
                className="w-full bg-gray-950 border border-gray-800 text-gray-100 px-4 py-3 focus:border-gray-600 focus:outline-none"
              >
                <option value="zh">中文 (zh)</option>
                <option value="en">English (en)</option>
              </select>
            </div>
          </div>

          {/* OG Image 上傳區 */}
          <div className="mb-6">
            <label className="block text-sm text-gray-400 mb-2">
              OG Image 圖片
              {ogImage && <span className="ml-2 text-green-500">✓ 已設定</span>}
            </label>
            
            {ogImage ? (
              <div className="relative inline-block">
                <div className="mb-2 text-xs text-green-500">
                  ✅ 圖片已載入
                </div>
                <img 
                  src={ogImage} 
                  alt="OG Image Preview" 
                  className="w-full max-w-md h-auto border border-gray-800 rounded"
                  onLoad={() => console.log('✅ 圖片成功渲染到 DOM')}
                  onError={(e) => {
                    console.error('❌ 圖片載入失敗:', ogImage)
                    console.error('錯誤事件:', e)
                  }}
                />
                <button
                  onClick={handleImageRemove}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  title="刪除圖片"
                >
                  <X size={16} />
                </button>
                <div className="mt-2 text-xs text-gray-500 break-all">
                  {ogImage}
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="hidden"
                  id="ogImageUpload"
                />
                <label
                  htmlFor="ogImageUpload"
                  className="flex items-center justify-center gap-2 w-full max-w-md px-6 py-12 bg-gray-950 border-2 border-dashed border-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <Upload size={20} />
                  {isUploading ? '上傳中...' : '點擊上傳圖片（建議尺寸 1200x630px）'}
                </label>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              💡 上傳後的圖片 URL 會自動儲存，建議尺寸為 1200x630px（支援 JPG、PNG、WEBP）
            </p>
          </div>

          {/* 大框框編輯區 */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-400">
                完整的 &lt;head&gt; 內容 *
              </label>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                <Eye size={16} />
                {showPreview ? "隱藏預覽" : "顯示預覽"}
              </button>
            </div>
            
            <textarea
              value={headContent}
              onChange={(e) => setHeadContent(e.target.value)}
              rows={30}
              placeholder={`直接貼上完整內容，例如：

<title>ONGU Coffee｜醞玖製研</title>
<meta name="description" content="..." />
<meta name="keywords" content="..." />

<!-- Open Graph -->
<meta property="og:title" content="..." />
<meta property="og:description" content="..." />
<meta property="og:image" content="..." />

<!-- Twitter Card -->
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="..." />

<!-- Schema -->
<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "CafeOrCoffeeShop",
  "name": "ONGU Studio - 信義店"
}
</script>`}
              className="w-full bg-gray-950 border border-gray-800 text-gray-100 px-4 py-3 font-mono text-sm focus:border-gray-600 focus:outline-none resize-y"
              spellCheck={false}
            />
          </div>

          {/* 提示訊息 */}
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700/30">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-200">
                <p className="font-medium mb-1">💡 使用提示</p>
                <ul className="list-disc list-inside space-y-1 text-yellow-300/80">
                  <li>直接從 HTML 文件複製整段 &lt;head&gt; 內容（不含 &lt;head&gt; 標籤本身）</li>
                  <li>包含 title、meta、script (JSON-LD) 等所有標籤</li>
                  <li>系統會在前端渲染時自動注入這些內容到 &lt;head&gt;</li>
                  <li>修改後記得點擊「儲存」按鈕</li>
                  <li>每個頁面路徑（URL）只能有一個 SEO 設定</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 預覽區 */}
          {showPreview && (
            <div className="mb-6 p-4 bg-gray-950 border border-gray-800">
              <h3 className="text-sm text-gray-400 mb-3">HTML 預覽</h3>
              <div className="bg-black p-4 overflow-x-auto">
                <pre className="text-xs text-gray-500 font-mono whitespace-pre-wrap break-all">
                  {headContent}
                </pre>
              </div>
            </div>
          )}

          {/* 操作按鈕 */}
          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={20} />
              {isSaving ? "儲存中..." : "儲存"}
            </button>

            {selectedPage !== "new" && (
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                <Trash2 size={20} />
                刪除
              </button>
            )}
          </div>
        </>
      )}

      {/* 空狀態提示 */}
      {!selectedPage && pages.length === 0 && (
        <div className="text-center py-12">
          <Plus size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 mb-4">尚無任何頁面 SEO 設定</p>
          <button
            onClick={() => handlePageSelect("new")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            新增第一個頁面
          </button>
        </div>
      )}
    </div>
  )
}

export default PageSEOManager
